'use client';

import React from 'react';

type Feature = {
  n: string;
  title: string;
  what: string;
  tech?: string;
  outcome: string;
  ref?: string;
};

type Module = {
  id: string;
  num: string;
  title: string;
  accent: string;
  glow: string;
  icon: string;
  tagline: string;
  features: Feature[];
};

const modules: Module[] = [
  {
    id: 'property-management',
    num: '01',
    title: 'Property Management AI',
    accent: '#A78BFA',
    glow: 'rgba(167,139,250,0.15)',
    icon: '🏢',
    tagline: 'Autonomous leasing, pricing, and retention.',
    features: [
      {
        n: '1.1',
        title: 'AI-Powered Unit Listings',
        what: 'Auto-generate marketing copy, optimize photo order, SEO-syndicate to Property24 / Zillow / Rightmove, virtual-stage empty units.',
        tech: 'LLM + Computer Vision',
        outcome: 'Listings go live 10× faster; higher CTR',
      },
      {
        n: '1.2',
        title: 'Dynamic Rent Pricing Agent',
        what: 'Continuously analyzes comps, seasonality, renewal patterns. Recommends per-unit rent adjustments within guardrails.',
        tech: 'ML regression + market APIs',
        outcome: 'Optimized revenue; fewer vacancy days',
      },
      {
        n: '1.3',
        title: 'Leasing Performer (Agentic)',
        what: '24/7 prospect replies via email/SMS/WhatsApp. Qualifies leads, schedules tours, runs credit/ID checks, pre-fills lease docs.',
        tech: 'Agentic LLM + CRM + DocuSign',
        outcome: '+73% lead-to-showing conversion',
        ref: 'AppFolio Realm-X benchmark',
      },
      {
        n: '1.4',
        title: 'Tenant Churn Risk Prediction',
        what: 'Flags at-risk tenants before renewal opens. Signals: unresolved tickets, late payments, portal drop-off.',
        tech: 'ML classification',
        outcome: '+5–15% renewal rate',
        ref: 'McKinsey 2026 projection',
      },
      {
        n: '1.5',
        title: 'Vacancy Forecasting',
        what: 'Predicts which units will be empty at 60 / 90 / 120 days. Triggers re-marketing before the unit is actually vacant.',
        tech: 'Time-series ML + churn model',
        outcome: 'Reduced vacancy loss',
      },
    ],
  },
  {
    id: 'accounting',
    num: '02',
    title: 'Accounting & Financials AI',
    accent: '#22C55E',
    glow: 'rgba(34,197,94,0.15)',
    icon: '💸',
    tagline: 'From data entry to autonomous close.',
    features: [
      {
        n: '2.1',
        title: 'Invoice OCR + Auto-Coding',
        what: 'Drag-drop invoice → extract vendor, amount, GL code, line items. Auto-match POs, flag discrepancies, label Verified / Warning.',
        tech: 'CV OCR + NLP + ML classifier',
        outcome: '3–5× AP throughput, <1% error',
        ref: 'Yardi Virtuoso: 60% AP speedup',
      },
      {
        n: '2.2',
        title: 'Bank Reconciliation Agent',
        what: 'Matches bank lines to ledger. Ranks candidates by confidence. Handles splits, partials, refunds. Learns from corrections.',
        tech: 'ML + fuzzy matching',
        outcome: 'Monthly rec: days → minutes',
      },
      {
        n: '2.3',
        title: 'Financial Forecasting',
        what: 'Projects cash flow 30/60/90/180 days. Flags anomalies (utility 3× normal → leak?). Drafts board-ready reports.',
        tech: 'ML time-series + LLM narrative',
        outcome: 'Fewer cash-flow surprises',
      },
      {
        n: '2.4',
        title: 'Month-End Close Agent',
        what: 'Coordinates accruals, reconciliations, allocations, JE drafts, CAM rec, straight-line rent. Escalates exceptions.',
        tech: 'Agentic LLM + rules engine',
        outcome: '20h → 5h per property',
        ref: 'Yardi Virtuoso benchmark',
      },
      {
        n: '2.5',
        title: 'Collections Agent',
        what: 'Monitors ledgers. Sends graduated reminders in tenant\'s preferred channel. Drafts settlements. Escalates to legal at the right time.',
        tech: 'Agentic LLM + jurisdictional rules',
        outcome: 'Lower delinquency, compliant process',
      },
      {
        n: '2.6',
        title: 'Fraud & Anomaly Detection',
        what: 'Duplicate payments, vendor-on-vendor fraud, synthetic IDs, altered pay stubs, wire-fraud red flags.',
        tech: 'ML anomaly + CV forensics',
        outcome: 'Reduced loss; regulatory compliance',
      },
    ],
  },
  {
    id: 'maintenance',
    num: '03',
    title: 'Maintenance & Facilities AI',
    accent: '#F59E0B',
    glow: 'rgba(245,158,11,0.15)',
    icon: '🛠',
    tagline: 'Predict, diagnose, dispatch — autonomously.',
    features: [
      {
        n: '3.1',
        title: 'Maintenance Performer',
        what: 'Tenant: "AC not cooling." AI asks clarifiers, reads photo, diagnoses, checks warranty, dispatches vendor with parts list, verifies completion.',
        tech: 'Agentic LLM + CV + vendor API',
        outcome: '40–60% faster resolution',
        ref: 'AppFolio + Yardi pattern',
      },
      {
        n: '3.2',
        title: 'Predictive Maintenance',
        what: 'IoT sensor data (HVAC, elevator, boiler) predicts failures 7–90 days out. Drone imagery for roof/facade. Lease-clause aware responsibility.',
        tech: 'ML LSTM + CV + MQTT',
        outcome: '20–30% maintenance cost cut',
        ref: 'Prologis / CBRE data',
      },
      {
        n: '3.3',
        title: 'Energy Optimization Agent',
        what: 'Ingests smart meters, HVAC, weather. Auto-tunes setpoints by occupancy. Flags hidden leaks. Produces GRESB / ENERGY STAR reports.',
        tech: 'ML + IoT + utility APIs',
        outcome: '10–20% utility savings + ESG',
      },
      {
        n: '3.4',
        title: 'Vendor Intelligence',
        what: 'Ranks vendors by resolution, cost, rework, ratings. Auto-assigns best-fit. Flags price drift. Maintains insurance/licensing compliance.',
        tech: 'ML ranking + OCR for docs',
        outcome: 'Lower cost, better quality, audit-ready',
      },
      {
        n: '3.5',
        title: 'Computer Vision Inspection',
        what: 'Move-in/out photo comparison, itemized damage list, deposit deduction report, periodic walk-through video analysis.',
        tech: 'GPT-4V / Claude Vision / Gemini',
        outcome: 'Consistent deposits, zero missed damage',
      },
    ],
  },
  {
    id: 'tenant',
    num: '04',
    title: 'Tenant / Resident Portal AI',
    accent: '#06B6D4',
    glow: 'rgba(6,182,212,0.15)',
    icon: '🧑‍💻',
    tagline: 'The 24/7 concierge residents actually love.',
    features: [
      {
        n: '4.1',
        title: 'Tenant AI Concierge',
        what: 'Answers any question — rent, lease clauses, pet policy, sublet rules — from their specific lease. Escalates only when it doesn\'t know.',
        tech: 'LLM + RAG over tenant docs',
        outcome: '70–80% resolution without a human',
      },
      {
        n: '4.2',
        title: 'Smart Rent Payment Assistant',
        what: 'Optimal-time reminders in preferred channel. Voice payments. Personalized payment plans within owner guardrails. Smart waivers.',
        tech: 'LLM + voice AI + ML personalization',
        outcome: 'Higher on-time rate',
      },
      {
        n: '4.3',
        title: 'Personalized Recommendations',
        what: 'Learns from portal behavior + surveys. Suggests amenities, events, renewal specials, referral bonuses tailored to each resident.',
        tech: 'Recommendation ML + LLM',
        outcome: 'Higher engagement, viral referrals',
      },
      {
        n: '4.4',
        title: 'Sentiment Monitoring',
        what: 'Every interaction analyzed for tone shifts before churn. PM gets alerted with specific recommended intervention.',
        tech: 'NLP classification + alerting',
        outcome: 'Early-warning CS; better NPS',
      },
      {
        n: '4.5',
        title: 'Accessibility AI',
        what: 'Real-time translation (30+ languages), voice interface for visually impaired, plain-language lease explanations.',
        tech: 'LLM translation + STT/TTS',
        outcome: 'ADA / POPIA / GDPR compliant',
        ref: 'MRI Agora: 30+ languages',
      },
    ],
  },
  {
    id: 'reporting',
    num: '05',
    title: 'Reporting & Analytics AI',
    accent: '#EC4899',
    glow: 'rgba(236,72,153,0.15)',
    icon: '📊',
    tagline: 'Ask Propmatic anything. Ship the answer.',
    features: [
      {
        n: '5.1',
        title: '"Ask Propmatic" — Natural Language BI',
        what: '"Which buildings will exceed budget next quarter?" → answer + chart + drill-down. "Show tenants whose rent-to-income worsened." → live table.',
        tech: 'LLM + text-to-SQL + viz engine',
        outcome: 'Entire company queries data without SQL',
        ref: 'MRI Ask Agora + Yardi MCP',
      },
      {
        n: '5.2',
        title: 'Automated Report Generation',
        what: 'Monthly owner reports, investor emails in owner\'s voice, board-ready slides, ESG reports (GRESB, ENERGY STAR, CSRD) — all auto-drafted.',
        tech: 'LLM + templates + chart rendering',
        outcome: 'Reporting: days → minutes',
      },
      {
        n: '5.3',
        title: 'Portfolio Benchmarking',
        what: 'Compares portfolio to anonymized market data. Identifies outliers (NOI lag, above-market OpEx). Recommends specific actions.',
        tech: 'ML clustering + analytics',
        outcome: 'Data-driven asset strategy',
      },
      {
        n: '5.4',
        title: 'Scenario Modeling & Digital Twin',
        what: 'What-if: refinance at current rates? Renovate vs divest? Energy upgrade ROI? Fed by real IoT data.',
        tech: 'LLM + financial engine + twin data',
        outcome: 'Better capital allocation',
        ref: 'Gartner: 50% of enterprises by 2028',
      },
      {
        n: '5.5',
        title: 'Anomaly Alerts',
        what: '"Utility bill at Building B jumped 45% — possible leak." "Work orders at Unit 7C are 3× normal — tenant may be problematic."',
        tech: 'ML anomaly + alert routing',
        outcome: 'Problems caught in days, not months',
      },
    ],
  },
  {
    id: 'lease',
    num: '06',
    title: 'Lease Management AI',
    accent: '#EF4444',
    glow: 'rgba(239,68,68,0.15)',
    icon: '📄',
    tagline: 'Turn PDFs into queryable intelligence.',
    features: [
      {
        n: '6.1',
        title: 'AI Lease Abstraction',
        what: 'Upload PDF → extract parties, dates, rent, escalations, CAM/NNN, co-tenancy, renewals, termination, use restrictions, default remedies.',
        tech: 'CV OCR + NLP + LLM + fine-tune',
        outcome: '95%+ time saved; <1% error',
        ref: 'JLL: $1M+ hidden escalations found',
      },
      {
        n: '6.2',
        title: 'Critical Date & Clause Monitoring',
        what: 'Tracks every renewal, expiry, option, notice period. Auto-reminds at strategic intervals. Flags risky clause combinations (co-tenancy cascades).',
        tech: 'Rules engine + LLM summary',
        outcome: 'Never miss a date; reduced exposure',
      },
      {
        n: '6.3',
        title: 'Compliance Engine — ASC 842 / IFRS 16 / GAAP',
        what: 'Auto-classifies operating vs finance. Calculates ROU assets & liabilities. Journal entries. Handles modifications, terminations, impairments.',
        tech: 'Rules engine + LLM reasoning',
        outcome: 'Audit-ready lease accounting',
      },
      {
        n: '6.4',
        title: 'Renewal Strategy Agent',
        what: 'Per upcoming renewal: proposes rent, concessions, term. Uses tenant history, market comp, churn score, unit condition. Drafts the offer.',
        tech: 'ML pricing + LLM drafting',
        outcome: 'Higher retained NOI',
      },
      {
        n: '6.5',
        title: 'Lease Query AI',
        what: '"Which leases allow subletting without consent?" "CPI-linked escalations over 5%?" "Co-tenancy tied to Store X?" — answered in seconds.',
        tech: 'LLM RAG over structured leases',
        outcome: 'Portfolio-wide legal intelligence',
      },
      {
        n: '6.6',
        title: 'Contract Redlining & Drafting',
        what: 'Compares new lease draft vs standard template, flags deviations, suggests counter-language from playbook. Drafts leases from brief.',
        tech: 'LLM + legal playbook library',
        outcome: 'Reduced legal cost; faster deals',
      },
    ],
  },
];

export default function AIModules() {
  return (
    <section id="modules" className="py-24 px-6 relative">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(124,58,237,0.1) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        <div className="reveal-blur mb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[rgba(124,58,237,0.25)] mb-5">
            <span className="w-1 h-1 rounded-full bg-[#A78BFA] status-pulse" />
            <span className="text-[10px] font-mono text-[#A78BFA] tracking-widest uppercase">
              Six AI Modules · 30+ Features
            </span>
          </div>
          <h2
            className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0]"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.75rem)' }}
          >
            Every workflow.
            <br />
            <span
              style={{
                background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              One agentic stack.
            </span>
          </h2>
        </div>

        <div className="flex flex-col gap-10">
          {modules.map((m) => (
            <div key={m.id} id={m.id} className="reveal-blur scroll-mt-24">
              {/* Module header */}
              <div className="flex items-start gap-5 mb-6">
                <div
                  className="w-16 h-16 shrink-0 rounded-2xl flex items-center justify-center text-3xl"
                  style={{
                    background: m.glow,
                    border: `1px solid ${m.accent}55`,
                    boxShadow: `0 0 30px ${m.glow}`,
                  }}
                >
                  {m.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[10px] font-mono tracking-widest uppercase"
                    style={{ color: m.accent }}
                  >
                    Module {m.num}
                  </div>
                  <h3 className="font-manrope font-extrabold text-2xl md:text-3xl text-[#EEEEF0] tracking-tight">
                    {m.title}
                  </h3>
                  <p className="text-sm text-[#9999A8] mt-1">{m.tagline}</p>
                </div>
              </div>

              {/* Feature grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
                {m.features.map((f) => (
                  <div
                    key={f.n}
                    className="reveal-blur bento-card card-shine p-5 flex flex-col gap-3"
                    style={{ borderColor: `${m.accent}33` }}
                  >
                    <div className="flex items-baseline gap-2">
                      <span
                        className="font-mono text-[11px] tracking-widest"
                        style={{ color: m.accent }}
                      >
                        {f.n}
                      </span>
                      <h4 className="font-manrope font-bold text-sm text-[#EEEEF0] tracking-tight">
                        {f.title}
                      </h4>
                    </div>
                    <p className="text-[13px] text-[#9999A8] leading-relaxed">{f.what}</p>

                    {f.tech && (
                      <div className="text-[10px] font-mono text-[#5A5A6E]">
                        <span className="text-[#5A5A6E]">TECH · </span>
                        <span className="text-[#9999A8]">{f.tech}</span>
                      </div>
                    )}

                    <div className="mt-auto pt-3 border-t border-[rgba(255,255,255,0.04)]">
                      <div
                        className="text-[11px] font-mono font-semibold"
                        style={{ color: m.accent }}
                      >
                        → {f.outcome}
                      </div>
                      {f.ref && (
                        <div className="text-[10px] font-mono text-[#5A5A6E] mt-1">
                          ref · {f.ref}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
